import React from 'react';
import { Input } from 'reactstrap';
import { LegalIssueOption } from './legal-issue-item';
import { LoiFormAnswerType } from './loi-form-answer-type.enum';
import { getLoiAnswerSelectOptions } from './render-loi-answer-readonly';

const COLLAPSED_TEXTAREA_HEIGHT_PX = 66;

const fitTextareaToContent = (el: HTMLInputElement) => {
    el.style.height = 'auto';
    const borderHeight = el.offsetHeight - el.clientHeight;
    const contentHeight = el.scrollHeight + borderHeight;
    el.style.height = `${Math.max(contentHeight, COLLAPSED_TEXTAREA_HEIGHT_PX)}px`;
};

export interface RenderLoiAnswerEditableParams {
    value: string | null;
    answerType: LoiFormAnswerType;
    options?: LegalIssueOption[];
    ariaLabel: string;
    disabled?: boolean;
    invalid?: boolean;
    className?: string;
    onChange: (value: string | null) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

export const renderLoiAnswerEditable = (
    params: RenderLoiAnswerEditableParams
): React.ReactNode => {
    const {
        value,
        answerType,
        options,
        ariaLabel,
        disabled = false,
        invalid = false,
        className = 'form-control loi-form-input',
        onChange,
        onFocus,
        onBlur,
    } = params;
    const resolvedValue = value ?? '';
    const commit = (nextValue: string) => onChange(nextValue === '' ? null : nextValue);

    switch (answerType) {
        case LoiFormAnswerType.Number:
            return (
                <Input
                    type="number"
                    value={resolvedValue}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    invalid={invalid}
                    onChange={(e) => commit(e.target.value)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={className}
                />
            );
        case LoiFormAnswerType.Date:
            return (
                <Input
                    type="date"
                    value={resolvedValue}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    invalid={invalid}
                    onChange={(e) => commit(e.target.value)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={className}
                />
            );
        case LoiFormAnswerType.PurchaseAgreementType:
        case LoiFormAnswerType.LeaseRateUponExtension:
            return (
                <Input
                    type="select"
                    value={resolvedValue}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    invalid={invalid}
                    onChange={(e) => commit(e.target.value)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className={className}>
                    <option value="">Select...</option>
                    {getLoiAnswerSelectOptions(answerType, options).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </Input>
            );
        default:
            return (
                <Input
                    type="textarea"
                    value={resolvedValue}
                    aria-label={ariaLabel}
                    readOnly={disabled}
                    invalid={invalid}
                    onFocus={(e) => {
                        fitTextareaToContent(e.target);
                        onFocus?.();
                    }}
                    onBlur={(e) => {
                        e.target.style.height = '';
                        onBlur?.();
                    }}
                    onChange={(e) => {
                        fitTextareaToContent(e.target);
                        commit(e.target.value);
                    }}
                    className={`${className} loi-form-value-textarea`}
                />
            );
    }
};
